/**
 * Returns the lowest start and the highest end within the list of ranges
 * @param ranges The ranges where to get the domain for
 * @returns The range that encapsulates the given range
 */
const flattenRanges = (ranges) => {
    return ranges && ranges.length > 0
        ? {
            start: (ranges.reduce((start, range) => start === undefined ? range.start : Math.min(start, range.start), undefined)),
            end: (ranges.reduce((end, range) => end === undefined ? range.end : Math.max(end, range.end), undefined)),
        }
        : undefined;
};
/**
 * This will calculate for each given data/rang item on which level it should
 * de layed out in order to not have a visual overlap. When the data given is
 * two dimensional, a flattened version will be created first. So the returned
 * layout is for the first dimension only
 * @param data The ranges to layout on the 'range axis'.
 * @returns The number of levels needed and for each range it's level index.
 */
export const layoutRanges = (data) => {
    let layout = { count: 0, levels: [] };
    if (data && data.length > 0) {
        //flatten the data when it is 2 dimensional
        let ranges = "length" in data[0]
            ? data
                .map((ranges) => flattenRanges(ranges))
                .filter((range) => range !== undefined)
            : data;
        const levelEndings = []; //keep track of the layout
        if (ranges.length > 0) {
            //there is data to be layed out
            layout.levels = ranges
                .map((rangeSet, index) => Object.assign({ index }, rangeSet))
                .sort((r0, r1) => r0.start - r1.start)
                .map((indexedSortedRange) => insertRange(indexedSortedRange, levelEndings))
                .sort((i0, i1) => i0.index - i1.index)
                .map((i) => i.level);
            layout.count = levelEndings.length;
        }
    }
    return layout;
};
/**
 * Inserts the given range in the existing perpendicular levels so no overlap
 * exists among the ranges. A new level is added when overlap will occur within
 * the existing levels.
 * @param range the range to add
 * @param levelEndings The set of current level endings within the layout.
 * @returns the level index where the range was added to together with the original index of the range
 */
const insertRange = (range, levelEndings) => {
    if (levelEndings.length === 0) {
        //no levels yet -> add first range on first level
        levelEndings.push(range.end);
        return { index: range.index, level: 0 };
    }
    else {
        //find first available level that can append the range without overlapping the last range in that level
        let levelIndex = levelEndings.findIndex((levelEnding) => levelEnding <= range.start);
        if (levelIndex === -1) {
            //if such a level does not exist, add a new level with the range as its data
            levelEndings.push(range.end);
            return { index: range.index, level: levelEndings.length - 1 };
        }
        else {
            //append the range to the level
            levelEndings[levelIndex] = range.end;
            return { index: range.index, level: levelIndex };
        }
    }
};
